'use client';

import { Button, cn } from '@shared/components/shadcn';
import { ChevronRight, LottolandSmileLogo } from '@shared/icons';
import { FRONTIFY_URL, useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import type { ComponentProps } from 'react';
import { IconBadge } from '../IconBadge/IconBadge';

type FeedbackBannerProps = {
  children?: React.ReactNode;
  onFeedbackClick?: () => void;
  namespace: string;
} & ComponentProps<'div'>;

function FeedbackBanner({ children, onFeedbackClick, className, namespace, ...props }: FeedbackBannerProps) {
  const t = useTranslations(namespace);
  const isMobile = useIsMobile({ breakpoint: 'md' });
  const BANNER_CLASSES = 'relative flex items-center justify-between p-5 h-20 bg-container text-foreground rounded-lg overflow-hidden';

  const handleBannerClick = () => {
    onFeedbackClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const allowedKeys = ['Enter', ' '];
    if (allowedKeys.includes(e.key)) {
      e.preventDefault();
      onFeedbackClick?.();
    }
  };

  return (
    <div className={cn(BANNER_CLASSES, className)} data-testid="feedback-banner" data-track-container="Feedback" {...props}>
      <div className="flex items-center flex-1 gap-4">
        <IconBadge icon={LottolandSmileLogo} shapeType="square" />
        <div className="flex flex-col gap-1 pr-5 md:pr-0">{children || t('message')}</div>
      </div>

      {isMobile && (
        <Button
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onClick={handleBannerClick}
          onKeyDown={handleKeyDown}
          aria-label={t('aria_label')}
          data-testid="feedback-banner-mobile-button"
        />
      )}

      <div className="hidden md:relative md:flex md:items-center md:justify-end md:h-20">
        <div className="absolute -right-10 top-0 bottom-0 w-64 flex-shrink-0 h-full">
          <Image
            src={`${FRONTIFY_URL}zxMt69wXK6k68HKvjnAm.svg`}
            alt="Decorative background"
            fill
            className="object-cover object-right"
            sizes="(min-width: 768px) 256px, 0px"
            priority={false}
          />
        </div>
        <div className="relative">
          <Button
            variant="secondary"
            onClick={e => {
              e.stopPropagation();
              onFeedbackClick?.();
            }}
            data-testid="feedback-button"
            aria-label={t('aria_label')}
          >
            {t('button')}
          </Button>
        </div>
      </div>

      <div className="md:hidden" aria-hidden="true">
        <ChevronRight size="regular" className="w-5 h-5" />
      </div>
    </div>
  );
}

export { FeedbackBanner, type FeedbackBannerProps };
